import { jsPDF } from 'jspdf';
import dayjs from 'dayjs';
import { DEJAVU_SANS_REGULAR, DEJAVU_SANS_BOLD } from './font/dejavu-sans-base64';

export interface PdfHeaderInfo {
  /** Název disciplíny / "Celkové výsledky" / "Dvojboj" */
  title: string;
  /** Název soutěže */
  competitionName: string;
  /** Datum + místo, např. "8. 6. 2025, Ostrava" */
  competitionDateLocation: string;
  /** Kategorie (např. "Mladší dorostenci") */
  categoryLabel?: string;
}

export interface PdfFooterInfo {
  headJudgeName?: string | null;
  eventLeaderName?: string | null;
  commissarName?: string | null;
}

export const PAGE = {
  margin: 15,
  contentWidth: 180,
  contentWidthLs: 267,
};

const COLOR = {
  primary: [198, 40, 40] as [number, number, number],
  text: [33, 33, 33] as [number, number, number],
  muted: [120, 120, 120] as [number, number, number],
};

/**
 * Inicializuje PDF s embedded DejaVu fontem (české znaky).
 */
export function initPdf(orientation: 'portrait' | 'landscape' = 'portrait'): jsPDF {
  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  doc.addFileToVFS('DejaVuSans.ttf', DEJAVU_SANS_REGULAR);
  doc.addFont('DejaVuSans.ttf', 'DejaVu', 'normal');
  doc.addFileToVFS('DejaVuSans-Bold.ttf', DEJAVU_SANS_BOLD);
  doc.addFont('DejaVuSans-Bold.ttf', 'DejaVu', 'bold');
  doc.setFont('DejaVu', 'normal');
  return doc;
}

/**
 * Vykresli hlavičku — 3 řádky centrované nahoře. Vrátí y kurzor pro pokračování.
 */
export function drawHeader(doc: jsPDF, info: PdfHeaderInfo): number {
  const w = doc.internal.pageSize.getWidth();
  let y = PAGE.margin + 5;

  doc.setFont('DejaVu', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...COLOR.primary);
  doc.text(info.title, w / 2, y, { align: 'center' });
  y += 8;

  doc.setFont('DejaVu', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR.text);
  doc.text(`${info.competitionName} — ${info.competitionDateLocation}`, w / 2, y, { align: 'center' });
  y += 6;

  if (info.categoryLabel) {
    doc.setFont('DejaVu', 'bold');
    doc.setFontSize(12);
    doc.text(info.categoryLabel, w / 2, y, { align: 'center' });
    y += 7;
  }
  return y + 3;
}

/**
 * Vykresli patičku s podpisy a časem tisku — volat na KAŽDÉ stránce v didDrawPage.
 */
export function drawFooter(doc: jsPDF, footer: PdfFooterInfo): void {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const y = h - PAGE.margin - 20;

  doc.setFont('DejaVu', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLOR.muted);
  doc.text(
    `Vytištěno: ${dayjs().format('DD.MM.YYYY HH:mm:ss')}  |  hasicionline-souteze.cz`,
    PAGE.margin,
    h - PAGE.margin - 5,
  );

  doc.setFontSize(9);
  doc.setTextColor(...COLOR.text);

  const colW = (w - 2 * PAGE.margin) / 3;
  const drawSign = (label: string, value: string | null | undefined, col: number) => {
    const x = PAGE.margin + col * colW + 5;
    doc.line(x, y, x + colW - 10, y);
    doc.setFont('DejaVu', 'normal');
    doc.text(label, x, y + 4);
    if (value) {
      doc.setFont('DejaVu', 'bold');
      doc.text(value, x, y + 9);
      doc.setFont('DejaVu', 'normal');
    }
  };
  drawSign('Hlavní rozhodčí', footer.headJudgeName, 0);
  drawSign('Velitel soutěže', footer.eventLeaderName, 1);
  drawSign('Komisař soutěže', footer.commissarName, 2);
}

/**
 * Vykresli "Strana N / M" — volat NA KONCI po vsech autoTable() callech.
 */
export function drawPageNumbers(doc: jsPDF): void {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('DejaVu', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.muted);
    doc.text(`Strana ${i} / ${total}`, w / 2, h - PAGE.margin - 5, { align: 'center' });
  }
}

/** Formátuje čas pokusu nebo "NP". timeMs null + isNp false → '—'. */
export function formatTime(timeMs: number | null | undefined, isNp: boolean): string {
  if (isNp) return 'NP';
  if (timeMs == null) return '—';
  return (timeMs / 1000).toFixed(2).replace('.', ',');
}

/**
 * Sanitizuje string pro filename (nahradi invalid chars za _).
 */
export function safeFilename(s: string): string {
  return s.replace(/[^\w\-_.]/g, '_').replace(/_+/g, '_');
}
